import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { authRedirectUrl } from "@/shell/authRedirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Footer } from "@/components/Footer";
import { useI18n } from "@/i18n/I18nContext";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authRedirectUrl("/reset-password"),
    });
    setLoading(false);
    if (error) {
      toast({ variant: "destructive", title: t("auth.error"), description: error.message });
    } else {
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary/10 blur-[120px]" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md px-4 relative z-10">
          <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
          </Link>

          <Card className="border-border/50 bg-card/80 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <NuvraLogo size={32} />
                <span className="font-bold text-xl text-foreground">Nuvra</span>
              </div>
              <CardTitle className="text-2xl">{t("auth.forgot.title")}</CardTitle>
              <CardDescription>
                {sent ? t("auth.forgot.subtitleSent") : t("auth.forgot.subtitlePrompt")}
              </CardDescription>
            </CardHeader>

            {sent ? (
              <CardContent className="text-center space-y-4 pb-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("auth.forgot.sentBefore")} <span className="font-medium text-foreground">{email}</span>{t("auth.forgot.sentAfter")}
                </p>
                <Button variant="outline" className="w-full" onClick={() => setSent(false)}>
                  {t("auth.forgot.tryAnother")}
                </Button>
              </CardContent>
            ) : (
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("auth.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex-col gap-4">
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t("auth.forgot.send")}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.forgot.remember")}{" "}
                    <Link to="/login" className="text-primary hover:underline">{t("auth.signIn")}</Link>
                  </p>
                </CardFooter>
              </form>
            )}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
